import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { ConversationMessage, ConversationPageResponse } from "@edu/shared";
import { ApiError } from "../../lib/api.ts";
import { Loading, LoadFailed } from "../../lib/loading.tsx";
import { useConversationNewsValue } from "../shell/ConversationNews.tsx";
import { announcementErrorText, REPLIES_CLOSED_TEXT } from "./announcement-text.ts";
import { ConversationCrumbs, ConversationMembers } from "./ConversationHeader.tsx";
import { ConversationMessageItem } from "./ConversationMessage.tsx";
import { ConversationSendForm } from "./ConversationSendForm.tsx";
import { getConversationPage, markConversationRead } from "./conversations-api.ts";

/**
 * Окно переписки: объявления и ответы вперемешку, в хронологии, как в
 * мессенджере (решение владельца 29.08.2026).
 *
 * ⚠️ **Сервер отдаёт свежие ПЕРВЫМИ и страницами по дюжине, и переворот на
 * экране.** Причина порядка записана у `ConversationPageResponse`: окно
 * открывается на свежем конце, значит первая страница обязана приезжать без
 * курсора. Отсюда три вещи, которых нет ни на одном другом экране кабинета:
 *
 * 1. приехавшая страница **разворачивается** перед отрисовкой, а более старая
 *    приписывается СВЕРХУ
 * 2. после первой страницы окно **проматывается вниз**, а после догрузки старых
 *    удерживается на прежнем месте: иначе прокрутка прыгала бы под пальцем
 * 3. догрузка идёт и от прокрутки вверх, и от кнопки. Кнопка обязательна:
 *    короткая переписка не прокручивается вовсе, и события прокрутки в ней не
 *    случится никогда
 *
 * ⚠️ **Свежесть приезжает от общего опроса, а не от своего таймера**
 * (`features/shell/ConversationNews.tsx`). Счётчик возит время последнего
 * сообщения разговора, и окно перечитывает себя ровно тогда, когда это время
 * ушло вперёд относительно показанного. Свой таймер здесь означал бы второй
 * опрос той же вкладки.
 *
 * ⚠️ **Прямых сообщений ученик-ученику нет ни одного**, и экран не создаёт даже
 * впечатления, что они возможны: ни «ответить Маше», ни имени в форме, ни
 * способа завести окно с кем бы то ни было. Окна заводятся отправкой, а
 * отправлять умеет только преподаватель.
 */

/**
 * Насколько близко к краю надо подойти, чтобы окно шевельнулось: у верхнего края
 * едет догрузка прошлого, у нижнего догоняется новое. Одно число на оба, потому
 * что вопрос один: «человек уже там».
 */
const NEAR_EDGE_PX = 64;

/**
 * Отказ вместе с тем, что его вызвало.
 *
 * ⚠️ **Повтор обязан повторять СЛОМАВШЕЕСЯ, а не начинать сначала** (правка по
 * итогам итогового ревью 30.08.2026). Кнопка повтора одна, а запросов за ней два
 * (перечитать свежий конец и догрузить прошлое), и до этой правки она всегда
 * звала первый: человек, у которого оборвалась догрузка на четвёртой странице
 * прошлого, нажимал «Повторить» и получал первую страницу без всего, что уже
 * догрузил.
 */
interface Failure {
  text: string;
  from: "reload" | "older";
}

export function ConversationPage() {
  const { conversationId = "" } = useParams();
  const navigate = useNavigate();
  const news = useConversationNewsValue();

  /** Шапка окна: заголовок, роль, куда писать. Приезжает с каждой страницей. */
  const [head, setHead] = useState<ConversationPageResponse | null>(null);
  /** Сообщения в ХРОНОЛОГИЧЕСКОМ порядке: сверху старое, снизу свежее. */
  const [items, setItems] = useState<ConversationMessage[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const [older, setOlder] = useState(false);

  /**
   * Живые ссылки на то, что меняется чаще самого окна.
   *
   * ⚠️ Через ref, а не через зависимости: `news` пересобирается на каждый
   * изменившийся ответ опроса, и `reload`, зависящий от него, пересоздавался бы
   * вместе с ним. Эффект, перечитывающий окно на смену ссылки на `reload`,
   * тогда стирал бы прокрутку и набранный текст каждые пять секунд.
   */
  const refreshNews = useRef(news.refresh);
  const watchNews = useRef(news.watch);
  /**
   * ⚠️ **Переставляются они ЭФФЕКТОМ, а не присваиванием в теле** (правка по
   * итогам итогового ревью 30.08.2026). Запись в ref во время рендера это
   * побочное действие в чистой части компонента: React 19 в строгом режиме
   * рисует дважды, а с конкурентной отрисовкой рендер вообще может быть
   * выброшен, и тогда ref остаётся от кадра, которого никто не увидел. Эффект
   * без списка зависимостей идёт после КАЖДОЙ отрисовки, и стоит он первым,
   * потому что соседние эффекты ниже эти же ссылки читают.
   */
  useEffect(() => {
    refreshNews.current = news.refresh;
    watchNews.current = news.watch;
  });
  /** Было ли окно уже показано: развилка «исчезло под читателем» ниже. */
  const opened = useRef(false);

  const box = useRef<HTMLDivElement | null>(null);
  /**
   * Что сделать с прокруткой сразу после отрисовки. Ref, а не состояние: это
   * приказ на один кадр, и вызывать им перерисовку нечего.
   */
  const scrollJob = useRef<{ bottom: true } | { height: number; top: number } | null>(null);
  /** Время самого свежего показанного сообщения: по нему решается, пора ли перечитать. */
  const topAt = useRef(0);

  const failText = useCallback((e: unknown): string => {
    return e instanceof ApiError
      ? announcementErrorText(e.code, e.message)
      : e instanceof Error
        ? e.message
        : String(e);
  }, []);

  /**
   * Перечитать свежий конец.
   *
   * ⚠️ Отметка о прочтении ставится ЗДЕСЬ и только здесь: человек открыл окно,
   * значит дочитал до свежего края. Отказ отметки проглатывается молча, как и у
   * прежней квитанции: это не работа человека, а наша запись о ней, и красная
   * строка о её неудаче сообщала бы о поломке, которой для его дела нет.
   */
  const reload = useCallback(async () => {
    try {
      const res = await getConversationPage(conversationId);
      const rows = [...res.items].reverse();
      setHead(res);
      setItems(rows);
      setCursor(res.nextCursor);
      setErr(null);
      topAt.current = rows.at(-1)?.createdAt ?? 0;
      scrollJob.current = { bottom: true };
      opened.current = true;
      void markConversationRead(conversationId)
        .then(() => refreshNews.current())
        .catch(() => undefined);
    } catch (e) {
      /*
        Разговор исчез, пока его читали: последнее объявление убрал сам читатель
        либо преподаватель в соседней вкладке. Показывать отказ на месте окна,
        которого больше нет, незачем, и человек уходит в список.
      */
      if (e instanceof ApiError && e.code === "conversation_not_found" && opened.current) {
        void navigate("/announcements", { replace: true });
        return;
      }
      setErr({ text: failText(e), from: "reload" });
    }
  }, [conversationId, failText, navigate]);

  const loadOlder = useCallback(async () => {
    if (cursor === null || older) return;
    setOlder(true);
    try {
      const res = await getConversationPage(conversationId, cursor);
      const node = box.current;
      // Удержать место под пальцем: запоминаем высоту и положение ДО того, как
      // сверху появятся новые строки
      if (node !== null) scrollJob.current = { height: node.scrollHeight, top: node.scrollTop };
      setItems((prev) => [...[...res.items].reverse(), ...prev]);
      setCursor(res.nextCursor);
      setErr(null);
    } catch (e) {
      setErr({ text: failText(e), from: "older" });
    } finally {
      setOlder(false);
    }
  }, [conversationId, cursor, older, failText]);

  /**
   * Сообщение убрали.
   *
   * ⚠️ **Ответ гасится НА МЕСТЕ, а перечитывается окно только после удаления
   * объявления** (правка по итогам итогового ревью 30.08.2026). Перечитывание
   * начинает окно с первой страницы, то есть роняет всё догруженное прошлое, а у
   * того, кто листал переписку за прошлый месяц и убрал там свою строчку, это
   * прыжок в конец. Ответ после удаления остаётся в окне надгробием, и как оно
   * выглядит, известно точно: тело сервер не отдаёт, время удаления проставляет.
   * Поэтому подставить его тут можно без второго похода.
   *
   * У ОБЪЯВЛЕНИЯ такой подстановки не бывает: вместе с ним из окна исчезают все
   * ответы под ним, а `replyToId` (куда класть следующие слова) уезжает на
   * предыдущее объявление. Это уже не правка строки, а другое окно.
   */
  const onRemoved = useCallback(
    (removed: ConversationMessage) => {
      if (removed.kind === "post") {
        void reload();
        return;
      }
      setItems((prev) =>
        prev.map((m) => (m.id === removed.id ? { ...m, body: null, deletedAt: Date.now() } : m)),
      );
      refreshNews.current();
    },
    [reload],
  );

  // Первый заход и смена адреса. `reload` пересоздаётся только вместе с
  // адресом, поэтому эффект и срабатывает ровно на смену окна
  useEffect(() => {
    setHead(null);
    setItems([]);
    setCursor(null);
    opened.current = false;
    void reload();
  }, [reload]);

  /**
   * Сказать общему опросу, какое окно читают.
   *
   * ⚠️ Без этого счётчик не возит строку разговора, стоящего дальше полусотни
   * самых свежих, то есть открытое на нём окно не узнаёт о новом сообщении
   * НИКОГДА. Разбор в `features/shell/ConversationNews.tsx`.
   */
  useEffect(() => {
    watchNews.current(conversationId);
    return () => watchNews.current(null);
  }, [conversationId]);

  /** Стоит ли человек у свежего края. Пустое окно считается свежим краем. */
  const atBottom = useCallback((): boolean => {
    const node = box.current;
    if (node === null) return true;
    return node.scrollHeight - node.scrollTop - node.clientHeight < NEAR_EDGE_PX;
  }, []);

  /**
   * Пришло новое сообщение.
   *
   * ⚠️ Сравнение со ВРЕМЕНЕМ показанного, а не с фактом ответа опроса: иначе окно
   * перечитывалось бы каждые пять секунд, стирая прокрутку и набранный текст.
   *
   * ⚠️ **И только когда человек стоит у свежего края.** Перечитывание сбрасывает
   * догруженное прошлое и уводит прокрутку вниз, а это ровно то, чего не должно
   * случиться с тем, кто листает переписку за прошлый месяц: сообщение пришло не
   * ему в руки, оно просто пришло. Что новое есть, он видит по числу у раздела, а
   * доедет оно ровно тогда, когда он вернётся вниз (ветка в `onScroll`).
   *
   * ⚠️ **И только после того, как окно приехало впервые** (правка по итогам
   * итогового ревью 30.08.2026). До неё этот эффект срабатывал на монтировании
   * наравне с соседним: счётчик к этому моменту уже знает время последнего
   * сообщения, а показанного времени ещё нет вовсе (`topAt` это ноль), то есть
   * условие выполнялось всегда. Открытие окна стоило двух `GET` страницы и двух
   * `POST` отметки о прочтении вместо одного и одного, и на канале 128 кбит/с
   * второй круг человек видит как подвисание.
   */
  const fresh = news.freshAt(conversationId);
  useEffect(() => {
    if (!opened.current) return;
    if (fresh !== null && fresh > topAt.current && atBottom()) void reload();
  }, [fresh, reload, atBottom]);

  useLayoutEffect(() => {
    const node = box.current;
    const job = scrollJob.current;
    if (node === null || job === null) return;
    scrollJob.current = null;
    node.scrollTop = "bottom" in job ? node.scrollHeight : node.scrollHeight - job.height + job.top;
  }, [items]);

  const onScroll = () => {
    const node = box.current;
    if (node === null) return;
    // Вернулся вниз, а пока он читал прошлое, пришло новое: догоняем
    if (opened.current && fresh !== null && fresh > topAt.current && atBottom()) {
      void reload();
      return;
    }
    if (cursor === null || older) return;
    if (node.scrollTop <= NEAR_EDGE_PX) void loadOlder();
  };

  if (head === null) {
    return (
      <main className="home home--form">
        <header className="head">
          <ConversationCrumbs here={null} />
        </header>
        {/* Отказ и ожидание не показываются вместе, и у отказа есть повтор:
            один приём на все экраны переписки и групп (`lib/loading.tsx`) */}
        {err === null ? <Loading /> : <LoadFailed text={err.text} onRetry={() => void reload()} />}
      </main>
    );
  }

  // Одно «сейчас» на всю отрисовку: времена соседних сообщений обязаны
  // считаться от одного момента, иначе на границе полуночи они разойдутся
  const now = Date.now();
  const title = head.title.trim() === "" ? "Без названия" : head.title;

  return (
    /*
      Ступень колонки у переписки та же, что у прозы: окно читают сверху вниз
      строками, а не колонками таблицы, и на ширине списка строка сообщения
      вырастала бы вдвое за комфортные девяносто знаков.
    */
    <main className="home home--form">
      {/* Место главного действия пустое: главное действие окна это «Отправить»,
          и живёт оно в подвале формы, под самой перепиской */}
      <header className="head">
        <ConversationCrumbs here={title} />
        <div className="head__line">
          <h1 className="head__h">{title}</h1>
        </div>
        {/*
          Что это группа, а не человек, сказано словом. Раскрытием это не
          становится: групповое окно существует у ученика только при видимом
          составе, а у скрытой группы окна группы у него нет вовсе.

          ⚠️ Плашки здесь нет и быть не должно: плашку получает состояние,
          которое может измениться, а переписка с группой человеком не станет
          никогда. Тот же приём стоит в шапке карточки группы, и записано там
          то же: в строке заголовка плашка соревнуется с самим заголовком.
        */}
        {head.kind === "group" && <div className="head__sub">Группа</div>}
      </header>

      {head.members !== undefined && <ConversationMembers names={head.members} />}

      {/* Запрос не удался, а показанное осталось на экране: повтор рядом, и
          повторяет он ровно то, что сломалось (разбор у `Failure`) */}
      {err !== null && (
        <LoadFailed
          text={err.text}
          onRetry={() => void (err.from === "older" ? loadOlder() : reload())}
        />
      )}

      <div className="chat" ref={box} onScroll={onScroll}>
        {cursor !== null && (
          <div className="chat__earlier">
            <button
              type="button"
              className="btn btn--quiet"
              disabled={older}
              onClick={() => void loadOlder()}
            >
              {older ? "Загружаем…" : "Более ранние сообщения"}
            </button>
          </div>
        )}
        <ul className="chat__list">
          {items.map((m) => (
            <ConversationMessageItem
              key={m.id}
              msg={m}
              teacher={head.myRole === "teacher"}
              now={now}
              onRemoved={onRemoved}
            />
          ))}
        </ul>
      </div>

      {/*
        ⚠️ Форма или объяснение вместо неё, а не выключенное поле с подсказкой:
        живая форма зовёт набрать текст, и отдать его будет некуда. Решает это
        сервер полем `canReply`, своего разбора состояния пары здесь нет.
      */}
      {head.canReply ? (
        <ConversationSendForm
          replyToId={head.replyToId}
          onSent={() => {
            void reload();
            refreshNews.current();
          }}
        />
      ) : (
        <p className="muted">{REPLIES_CLOSED_TEXT}</p>
      )}
    </main>
  );
}
